package uz.mint.frontend.components.sections

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.w3c.dom.events.Event
import org.w3c.dom.events.KeyboardEvent
import uz.mint.frontend.PageGutter
import uz.mint.frontend.alignItems
import uz.mint.frontend.alignSelf
import uz.mint.frontend.api.AccountApi
import uz.mint.frontend.api.CustomerEsim
import uz.mint.frontend.components.widgets.CloseIcon
import uz.mint.frontend.components.widgets.QrIcon
import uz.mint.frontend.flexWrapCss
import uz.mint.frontend.justifyContent
import uz.mint.frontend.marginInlineAuto
import uz.mint.frontend.wordBreakAll
import uz.mint.frontend.i18n.AccountStrings
import uz.mint.frontend.i18n.Locale
import uz.mint.frontend.i18n.accountStringsFor
import uz.mint.frontend.i18n.installGuideFor
import uz.mint.frontend.i18n.stringsFor
import uz.mint.frontend.toSitePalette

/**
 * My eSIMs.
 *
 * The data is the provider's — status, usage, the LPA activation string behind the QR — but the
 * *list* is ours: the backend answers only for profiles linked to this customer, because
 * eSIMAccess holds every profile we have ever sold in one account and would return any of them
 * to a request that named an ICCID.
 */
@Composable
fun MyEsimsPage(locale: Locale) {
    val account = accountStringsFor(locale)

    AccountShell(locale, account.esimsTitle, account.esimsLede) { _, strings ->
        EsimList(locale, strings)
    }
}

@Composable
private fun EsimList(locale: Locale, account: AccountStrings) {
    val palette = ColorMode.current.toSitePalette()
    val scope = rememberCoroutineScope()

    var esims by remember { mutableStateOf<List<CustomerEsim>?>(null) }
    var failed by remember { mutableStateOf(false) }
    var attempt by remember { mutableStateOf(0) }
    var refreshing by remember { mutableStateOf<String?>(null) }
    // Which eSIM's QR is open full-screen. Held here rather than inside the card so only one
    // can ever be open, and so it survives the list being replaced by a refresh underneath it.
    var showQrFor by remember { mutableStateOf<CustomerEsim?>(null) }

    showQrFor?.let { QrOverlay(it, account, locale) { showQrFor = null } }

    LaunchedEffect(attempt) {
        failed = false
        runCatching { AccountApi.esims() }
            .onSuccess { esims = it }
            .onFailure { failed = true }
    }

    when {
        failed -> AccountCard {
            SpanText(account.esimsError, Modifier.fontSize(15.px))
            uz.mint.frontend.components.widgets.SecondaryButton(account.esimsRetry) { attempt++ }
        }

        esims == null -> SpanText(
            account.esimsLoading,
            Modifier.fontSize(15.px).color(palette.ink2),
        )

        esims!!.isEmpty() -> AccountCard {
            SpanText(account.esimsEmpty, Modifier.fontSize(16.px).fontWeight(FontWeight.Bold))
            Link(
                path = locale.plansPath,
                modifier = Modifier
                    .alignSelfStart()
                    .backgroundColor(palette.solidBg)
                    .color(palette.solidFg)
                    .fontWeight(FontWeight.Bold)
                    .fontSize(15.px)
                    .padding(topBottom = 13.px, leftRight = 22.px),
                variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
            ) { SpanText(account.esimsEmptyCta) }
        }

        else -> Column(Modifier.fillMaxWidth().gap(20.px)) {
            esims!!.forEach { esim ->
                EsimCard(
                    esim = esim,
                    account = account,
                    onShowQr = { showQrFor = esim },
                    refreshing = refreshing == esim.iccid,
                    onRefresh = {
                        if (refreshing == null) {
                            refreshing = esim.iccid
                            scope.launch {
                                runCatching { AccountApi.refreshEsim(esim.iccid) }
                                    .onSuccess { updated ->
                                        esims = esims!!.map { if (it.iccid == updated.iccid) updated else it }
                                    }
                                refreshing = null
                            }
                        }
                    },
                )
            }
        }
    }
}

@Composable
internal fun EsimCard(
    esim: CustomerEsim,
    account: AccountStrings,
    onShowQr: () -> Unit,
    refreshing: Boolean,
    onRefresh: () -> Unit,
) {
    val palette = ColorMode.current.toSitePalette()
    var copied by remember { mutableStateOf(false) }

    AccountCard {
        Row(Modifier.fillMaxWidth().gap(16.px).flexWrapCss(), verticalAlignment = Alignment.Top) {
            Column(Modifier.gap(6.px)) {
                SpanText(
                    esim.planName ?: esim.iccid,
                    Modifier.fontSize(20.px).fontWeight(FontWeight.ExtraBold).letterSpacing((-0.02).em),
                )
                SpanText(
                    "${account.esimStatusLabel}: ${account.esimStatus(esim.esimStatus ?: esim.smdpStatus)}",
                    Modifier.fontSize(14.px).color(palette.ink2),
                )
            }
            Spacer()
            uz.mint.frontend.components.widgets.SecondaryButton(
                if (refreshing) account.esimRefreshing else account.esimRefresh,
                onRefresh,
            )
        }

        UsageBar(esim, account)

        Row(Modifier.fillMaxWidth().gap(24.px).flexWrapCss()) {
            esim.expiresAt?.let {
                Fact(account.esimExpires, formatDate(it))
            }
            esim.daysLeft?.let {
                Fact("", if (it <= 0) account.esimExpired else account.withCount(account.esimDaysLeft, it))
            }
            esim.orderReference?.let { Fact(account.esimOrderRef, it) }
        }

        // The activation string is the product: without it the customer has paid for nothing
        // they can install.
        if (esim.activationCode.isNullOrBlank()) {
            SpanText(account.esimPending, Modifier.fontSize(14.px).color(palette.ink2).lineHeight(1.5))
        } else {
            // The QR first, the installation page second: scanning is how an eSIM is normally
            // installed, and the link is what answers the phone that cannot scan — which is the
            // one this page is open on.
            QrButton(account, onShowQr)

            InstallSection(esim, account, copied) {
                window.navigator.clipboard.writeText(esim.activationCode)
                copied = true
            }
        }
    }
}

/**
 * The way into the QR: an icon and a label, sized as one press target.
 *
 * A button rather than the QR itself rendered small on the card. A thumbnail invites a customer
 * to aim a camera at something too small to decode — the modules of a 90px QR are sub-pixel on a
 * phone — and failing to scan reads as a broken code rather than as a too-small picture.
 */
@Composable
private fun QrButton(account: AccountStrings, onClick: () -> Unit) {
    val palette = ColorMode.current.toSitePalette()
    Button(
        attrs = Modifier
            .alignSelfStart()
            .display(DisplayStyle.Flex).alignItems("center").gap(12.px)
            .padding(topBottom = 13.px, leftRight = 20.px)
            .border(0.px, LineStyle.None, palette.rule)
            .backgroundColor(palette.solidBg).color(palette.solidFg)
            .fontWeight(FontWeight.Bold).fontSize(15.px).fontFamily("inherit")
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .toAttrs()
    ) {
        QrIcon(size = 20, color = palette.solidFg)
        SpanText(account.esimQrShow)
    }
}

/**
 * How to install this profile: the provider's page if we have it, the raw LPA string if we do not.
 *
 * **The link is preferred over printing the code.** `LPA:1$rsp-eu.simlessly.com$0A4455DC…` is a
 * hundred characters that mean nothing to a customer, and pasting it correctly on a phone is a
 * worse first minute than opening a page. The provider's page carries the QR *and* the SM-DP+
 * details for manual entry, so nothing is lost by not showing the string here.
 *
 * The code stays as the fallback, because `shortUrl` is not guaranteed: an older row synced before
 * we stored it, or a provider that omits it, still has to leave the customer able to install.
 */
@Composable
private fun InstallSection(
    esim: CustomerEsim,
    account: AccountStrings,
    copied: Boolean,
    onCopy: () -> Unit,
) {
    val palette = ColorMode.current.toSitePalette()
    val link = esim.shortUrl?.takeIf { it.isNotBlank() }

    Column(Modifier.fillMaxWidth().gap(8.px)) {
        if (link != null) {
            A(
                href = link,
                attrs = Modifier
                    .alignSelfStart()
                    .border(2.px, LineStyle.Solid, palette.rule)
                    .color(palette.ink)
                    .fontWeight(FontWeight.Bold).fontSize(15.px)
                    .padding(topBottom = 13.px, leftRight = 20.px)
                    .textDecorationLine(TextDecorationLine.None)
                    .toAttrs {
                        // A new tab, and `noopener` with it: the page belongs to the provider, and
                        // a customer sent away from their account mid-install rarely finds the way
                        // back.
                        attr("target", "_blank")
                        attr("rel", "noopener noreferrer")
                    },
            ) { SpanText(account.esimInstallLink) }

            SpanText(
                account.esimInstallLinkHelp,
                Modifier.fontSize(13.px).color(palette.ink2).lineHeight(1.5),
            )
        } else {
            SpanText(
                account.esimActivation,
                Modifier.fontSize(12.px).fontWeight(FontWeight.Bold).letterSpacing(0.08.em)
                    .textTransform(TextTransform.Uppercase).color(palette.ink2),
            )
            Div(
                Modifier
                    .fillMaxWidth()
                    .border(2.px, LineStyle.Solid, palette.rule)
                    .backgroundColor(palette.paper)
                    .padding(12.px)
                    .fontSize(13.px)
                    .fontFamily("ui-monospace", "SFMono-Regular", "Menlo", "monospace")
                    // LPA strings are long and unbreakable; without this the card scrolls
                    // sideways on a phone.
                    .wordBreakAll()
                    .toAttrs()
            ) { SpanText(esim.activationCode.orEmpty()) }

            Row(Modifier.gap(14.px).flexWrapCss(), verticalAlignment = Alignment.CenterVertically) {
                TextAction(if (copied) account.esimCopied else account.esimCopy, palette.ink, onCopy)
                SpanText(
                    account.esimActivationHelp,
                    Modifier.fontSize(13.px).color(palette.ink2).lineHeight(1.5),
                )
            }
        }
    }
}

/**
 * One eSIM, owning its own refresh and QR state.
 *
 * For pages that show a single profile — the order detail page — where the list page's rule of
 * "one refresh at a time across the whole list" has nothing to coordinate.
 */
@Composable
internal fun SingleEsimCard(esim: CustomerEsim, account: AccountStrings, locale: Locale) {
    val scope = rememberCoroutineScope()
    var current by remember(esim.iccid) { mutableStateOf(esim) }
    var refreshing by remember(esim.iccid) { mutableStateOf(false) }
    var showQr by remember(esim.iccid) { mutableStateOf(false) }

    if (showQr) QrOverlay(current, account, locale) { showQr = false }

    EsimCard(
        esim = current,
        account = account,
        onShowQr = { showQr = true },
        refreshing = refreshing,
        onRefresh = {
            if (!refreshing) {
                refreshing = true
                scope.launch {
                    runCatching { AccountApi.refreshEsim(current.iccid) }.onSuccess { current = it }
                    refreshing = false
                }
            }
        },
    )
}

/**
 * The QR, as large as the screen allows.
 *
 * Full-screen because the customer is holding a second phone at it: a camera needs the modules
 * to be a few pixels each, and a code inside a card on a scrolling page is not that. The image
 * comes from the backend rather than being drawn here — the LPA string is the only input, and
 * generating a QR in the browser would mean shipping an encoder to every visitor of every page.
 *
 * The activation string stays underneath, because this view has to answer the case the QR cannot:
 * when the site is open on the very phone the eSIM is for, there is no second screen to scan.
 */
@Composable
internal fun QrOverlay(esim: CustomerEsim, account: AccountStrings, locale: Locale, onClose: () -> Unit) {
    val palette = ColorMode.current.toSitePalette()
    var failed by remember(esim.iccid) { mutableStateOf(false) }
    var copied by remember(esim.iccid) { mutableStateOf(false) }
    var guide by remember { mutableStateOf(false) }

    // The instructions **replace** the QR rather than stacking on top of it: the legal reader sits
    // at a lower z-index, so an overlay over an overlay would render behind the thing that opened
    // it. Closing the guide comes straight back here, with the QR still loaded.
    if (guide) {
        val strings = stringsFor(locale)
        LegalOverlay(strings.footerL4, installGuideFor(locale), strings) { guide = false }
        return
    }

    // Esc closes it. The only other full-screen surface on the site is the checkout sheet, which
    // predates this and has no handler; a QR is looked at and dismissed far more often, and on a
    // desktop the close button is a long way from where the eye is.
    DisposableEffect(Unit) {
        val onKey: (Event) -> Unit = { event ->
            if ((event as? KeyboardEvent)?.key == "Escape") onClose()
        }
        window.addEventListener("keydown", onKey)
        onDispose { window.removeEventListener("keydown", onKey) }
    }

    Div(
        Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .zIndex(95)
            .backgroundColor(palette.paper).color(palette.ink)
            .overflow { y(Overflow.Auto) }
            .toAttrs {
                attr("role", "dialog")
                attr("aria-modal", "true")
                attr("aria-label", account.esimQrTitle)
            }
    ) {
        Row(
            Modifier
                .fillMaxWidth()
                .position(Position.Sticky).top(0.px).zIndex(2)
                .backgroundColor(palette.paper)
                .borderBottom(2.px, LineStyle.Solid, palette.rule)
                .gap(20.px).padding(topBottom = 16.px, leftRight = PageGutter),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            SpanText(
                account.esimQrTitle,
                Modifier.fontSize(clamp(18.px, 2.vw, 24.px)).fontWeight(FontWeight.ExtraBold)
                    .letterSpacing((-0.025).em),
            )
            Spacer()
            Button(
                attrs = Modifier
                    .size(46.px)
                    .border(2.px, LineStyle.Solid, palette.rule)
                    .display(DisplayStyle.Grid)
                    .justifyContent("center").alignItems("center")
                    .flexShrink(0)
                    .backgroundColor(Colors.Transparent)
                    .cursor(Cursor.Pointer)
                    .onClick { onClose() }
                    .toAttrs { attr("aria-label", account.esimQrClose) }
            ) { CloseIcon(color = palette.ink) }
        }

        Column(
            Modifier
                .fillMaxWidth().maxWidth(560.px).marginInlineAuto()
                .padding(top = clamp(20.px, 3.vw, 36.px), bottom = 64.px, leftRight = PageGutter)
                .gap(20.px),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            esim.planName?.let {
                SpanText(
                    it,
                    Modifier.fontSize(18.px).fontWeight(FontWeight.ExtraBold).textAlign(TextAlign.Center),
                )
            }

            if (failed) {
                SpanText(account.esimQrError, Modifier.fontSize(15.px).color(palette.ink2))
            } else {
                // White padding around the symbol on top of the SVG's own quiet zone: the card
                // behind it is dark in dark mode, and a scanner reads contrast, not colours.
                Div(
                    Modifier
                        .backgroundColor(Colors.White)
                        .border(2.px, LineStyle.Solid, palette.rule)
                        .padding(14.px)
                        .lineHeight(0)
                        .toAttrs()
                ) {
                    Img(
                        src = AccountApi.esimQrUrl(esim.iccid),
                        attrs = Modifier
                            .display(DisplayStyle.Block)
                            // Square, and as big as the narrower of the two axes allows: on a
                            // phone that is the width, on a laptop the height.
                            .width(clamp(220.px, 78.vw, 360.px))
                            .height(clamp(220.px, 78.vw, 360.px))
                            .toAttrs {
                                attr("alt", account.esimQrTitle)
                                addEventListener("error") { failed = true }
                            },
                    )
                }
            }

            SpanText(
                account.esimQrHelp,
                Modifier.fontSize(15.px).lineHeight(1.5).textAlign(TextAlign.Center),
            )
            SpanText(
                account.esimQrSamePhone,
                Modifier.fontSize(13.px).lineHeight(1.5).color(palette.ink2).textAlign(TextAlign.Center),
            )

            // Same rule as the card: the provider's page if we have it, the raw string only when
            // we do not. This is the view a customer reaches when the QR cannot help them —
            // because the site is open on the very phone being set up — so the way forward has to
            // be here rather than a screen back.
            InstallSection(esim, account, copied) {
                esim.activationCode?.let { window.navigator.clipboard.writeText(it) }
                copied = true
            }

            // The step-by-step guide the footer already carries, offered where it is actually
            // needed: a customer looking at a QR is about to install, and "Настройки → Сотовая
            // связь → Добавить eSIM" is not something anyone finds twice.
            TextAction(account.esimInstallGuide, palette.accentText) { guide = true }
        }
    }
}

/**
 * Data allowance as a bar.
 *
 * Hidden entirely when the plan states no allowance rather than drawn empty — a full-width
 * empty bar reads as "no data left", which is the opposite of what an unknown total means.
 */
@Composable
internal fun UsageBar(esim: CustomerEsim, account: AccountStrings) {
    val palette = ColorMode.current.toSitePalette()
    val fraction = esim.usedFraction ?: return

    Column(Modifier.fillMaxWidth().gap(8.px)) {
        Row(Modifier.fillMaxWidth().gap(12.px).flexWrapCss(), verticalAlignment = Alignment.CenterVertically) {
            SpanText(
                "${account.esimData}: ${formatData(esim.dataUsedMb ?: 0)} / ${formatData(esim.dataTotalMb ?: 0)}",
                Modifier.fontSize(14.px).fontWeight(FontWeight.SemiBold),
            )
            Spacer()
            esim.dataRemainingMb?.let {
                SpanText(
                    "${formatData(it)} ${account.esimRemaining}",
                    Modifier.fontSize(14.px).fontWeight(FontWeight.Bold).color(palette.accentText),
                )
            }
        }
        Div(
            Modifier
                .fillMaxWidth()
                .height(10.px)
                .backgroundColor(palette.rule)
                .toAttrs()
        ) {
            Div(
                Modifier
                    .height(10.px)
                    .width((fraction * 100).percent)
                    .backgroundColor(palette.mint)
                    .toAttrs()
            ) {}
        }
    }
}

@Composable
private fun Fact(label: String, value: String) {
    val palette = ColorMode.current.toSitePalette()
    Column(Modifier.gap(3.px)) {
        if (label.isNotEmpty()) {
            SpanText(
                label,
                Modifier.fontSize(11.px).fontWeight(FontWeight.Bold).letterSpacing(0.08.em)
                    .textTransform(TextTransform.Uppercase).color(palette.ink2),
            )
        }
        SpanText(value, Modifier.fontSize(15.px).fontWeight(FontWeight.SemiBold))
    }
}

/** `5120` → `5 GB`, `700` → `700 MB`. Whole gigabytes only; halves read as noise here. */
internal fun formatData(mb: Int): String =
    if (mb >= 1024 && mb % 1024 == 0) "${mb / 1024} GB" else "$mb MB"

/**
 * ISO instant → `31.07.2026`.
 *
 * Sliced rather than parsed: the backend always sends a UTC ISO-8601 instant, and pulling in a
 * date library to reformat ten characters is not worth the bundle.
 */
internal fun formatDate(iso: String): String {
    val date = iso.substringBefore('T')
    val parts = date.split('-')
    return if (parts.size == 3) "${parts[2]}.${parts[1]}.${parts[0]}" else date
}

private fun Modifier.alignSelfStart() = alignSelf("flex-start")
